/**
 * Reference ("foundational") map layers: admin-uploaded boundaries, districts,
 * infrastructure, POIs, routes... shared by every user and drawn with a
 * per-layer style. Mirrors backend app/api/routes/reference_layers.py.
 */
import { apiService } from './api';
import { formatCameraOverlayApiError } from './cameraOverlayApi';

const http = apiService.client;

export type ReferenceGeometryKind = 'point' | 'line' | 'polygon' | 'mixed';
export type ReferenceLayerType = 'boundaries' | 'districts' | 'infrastructure' | 'poi' | 'routes' | 'custom';

export const REFERENCE_LAYER_TYPE_LABELS: Record<ReferenceLayerType, string> = {
  boundaries: 'Boundaries',
  districts: 'Districts / zones',
  infrastructure: 'Infrastructure',
  poi: 'Points of interest',
  routes: 'Routes / corridors',
  custom: 'Custom',
};

export type ColorByMode = 'ramp' | 'categorical';

export type ReferenceColorBy = {
  field: string;
  mode: ColorByMode;
  ramp: string;
  reverse: boolean;
};

/** Mirrors backend default_style(); every key is always present after normalization. */
export type ReferenceLayerStyle = {
  color: string;
  fill_opacity: number;
  stroke_color: string;
  stroke_width: number;
  stroke_opacity: number;
  point_radius: number;
  point_opacity: number;
  labels: boolean;
  label_size: number;
  color_by: ReferenceColorBy | null;
  min_zoom: number;
};

export type ReferenceFieldStat = {
  kind: 'number' | 'string';
  count: number;
  distinct: number;
  min?: number;
  max?: number;
  /** Present when distinct ≤ 40 (categorical styling). */
  values?: string[];
};

export type ReferenceLayerDto = {
  id: number;
  name: string;
  description?: string | null;
  layer_type: ReferenceLayerType;
  geometry_kind: ReferenceGeometryKind;
  feature_count: number;
  bbox?: [number, number, number, number] | null;
  style: ReferenceLayerStyle;
  label_field?: string | null;
  fields: Record<string, ReferenceFieldStat>;
  source?: string | null;
  source_format?: string | null;
  active: boolean;
  sort_order: number;
  uploaded_by?: string | null;
  uploaded_at: string;
  updated_at: string;
};

export type ReferenceLayerPreviewDto = {
  file_name: string;
  suggested_name: string;
  source_format: string;
  source_format_label: string;
  geometry_kind: ReferenceGeometryKind;
  feature_count: number;
  skipped: number;
  bbox?: [number, number, number, number] | null;
  fields: Record<string, ReferenceFieldStat>;
  label_field?: string | null;
  style: ReferenceLayerStyle;
  warnings: string[];
  sample: Array<{ type: 'Feature'; geometry: { type: string }; properties: Record<string, unknown> }>;
};

export type ReferenceLayerOptionsDto = {
  extensions: string[];
  formats: Record<string, string>;
  layer_types: Record<string, string>;
  geometry_kinds: string[];
  ramps: Record<string, string[]>;
  categorical_palette: string[];
  max_bytes: number;
};

export type ReferenceLayerUpdate = Partial<{
  name: string;
  description: string | null;
  layer_type: ReferenceLayerType;
  style: Partial<ReferenceLayerStyle>;
  label_field: string | null;
  source: string | null;
  active: boolean;
  sort_order: number;
}>;

export type ReferenceLayerImportOptions = {
  name?: string | null;
  description?: string | null;
  layer_type?: ReferenceLayerType | null;
  label_field?: string | null;
  style?: Partial<ReferenceLayerStyle> | null;
  source?: string | null;
  /** CSV / JSON rows: which columns hold latitude / longitude. */
  mapping?: { latitude?: string | null; longitude?: string | null } | null;
};

export type ReferenceFeatureCollection = GeoJSON.FeatureCollection<GeoJSON.Geometry, Record<string, unknown>>;

/** Mirrors backend COLOR_RAMPS so the editor can preview without a round-trip. */
export const REFERENCE_COLOR_RAMPS: Record<string, string[]> = {
  viridis: ['#440154', '#3b528b', '#21918c', '#5ec962', '#fde725'],
  magma: ['#000004', '#51127c', '#b73779', '#fc8961', '#fcfdbf'],
  blues: ['#deebf7', '#9ecae1', '#4292c6', '#2171b5', '#08306b'],
  reds: ['#fee5d9', '#fcae91', '#fb6a4a', '#de2d26', '#a50f15'],
  greens: ['#e5f5e0', '#a1d99b', '#41ab5d', '#238b45', '#00441b'],
  oranges: ['#feedde', '#fdbe85', '#fd8d3c', '#e6550d', '#a63603'],
  spectral: ['#d53e4f', '#fc8d59', '#ffffbf', '#99d594', '#3288bd'],
  rdylgn: ['#d73027', '#fc8d59', '#fee08b', '#91cf60', '#1a9850'],
};

export const REFERENCE_CATEGORICAL_PALETTE: string[] = [
  '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948', '#b07aa1',
  '#ff9da7', '#9c755f', '#bab0ac', '#1f77b4', '#d62728', '#2ca02c', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf', '#aec7e8',
];

export const REFERENCE_LAYER_ACCEPT =
  '.geojson,.json,.kml,.kmz,.zip,.gpx,.osm,.xml,.csv,.tsv,.txt,' +
  'application/geo+json,application/json,application/zip,application/x-zip-compressed,' +
  'application/vnd.google-earth.kml+xml,application/vnd.google-earth.kmz,text/csv,text/xml';

export const REFERENCE_LAYER_EXTENSIONS = [
  '.geojson', '.json', '.kml', '.kmz', '.zip', '.gpx', '.osm', '.xml', '.csv', '.tsv', '.txt',
];

export function isSupportedReferenceLayerFile(name: string): boolean {
  const lower = name.toLowerCase();
  return REFERENCE_LAYER_EXTENSIONS.some((ext) => lower.endsWith(ext));
}

export const formatReferenceLayerApiError = formatCameraOverlayApiError;

export function geometryKindLabel(kind: ReferenceGeometryKind): string {
  switch (kind) {
    case 'point':
      return 'Points';
    case 'line':
      return 'Lines';
    case 'polygon':
      return 'Polygons';
    default:
      return 'Mixed';
  }
}

export async function listReferenceLayers(): Promise<ReferenceLayerDto[]> {
  const { data } = await http.get<{ layers: ReferenceLayerDto[] }>('/reference-layers');
  return data.layers ?? [];
}

export async function fetchReferenceLayerOptions(): Promise<ReferenceLayerOptionsDto> {
  const { data } = await http.get<ReferenceLayerOptionsDto>('/reference-layers/options');
  return data;
}

export async function fetchReferenceLayerGeoJson(id: number): Promise<ReferenceFeatureCollection> {
  const { data } = await http.get<ReferenceFeatureCollection>(`/reference-layers/${id}/geojson`);
  return data?.type === 'FeatureCollection' ? data : { type: 'FeatureCollection', features: [] };
}

export async function updateReferenceLayer(id: number, patch: ReferenceLayerUpdate): Promise<ReferenceLayerDto> {
  const { data } = await http.patch<ReferenceLayerDto>(`/reference-layers/${id}`, patch);
  return data;
}

export async function deleteReferenceLayer(id: number): Promise<void> {
  await http.delete(`/reference-layers/${id}`);
}

function multipartHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const token = localStorage.getItem('access_token');
  if (token) headers.Authorization = `Bearer ${token}`;
  if (process.env.NODE_ENV === 'development') {
    const devUser = localStorage.getItem('dev_user');
    if (devUser) headers['X-Dev-User'] = devUser;
  }
  return headers;
}

function buildForm(file: File, opts: ReferenceLayerImportOptions = {}): FormData {
  const form = new FormData();
  form.append('file', file);
  if (opts.name && opts.name.trim()) form.append('name', opts.name.trim());
  if (opts.description && opts.description.trim()) form.append('description', opts.description.trim());
  if (opts.layer_type) form.append('layer_type', opts.layer_type);
  if (opts.label_field && opts.label_field.trim()) form.append('label_field', opts.label_field.trim());
  if (opts.source && opts.source.trim()) form.append('source', opts.source.trim());
  if (opts.style) form.append('style', JSON.stringify(opts.style));
  if (opts.mapping && (opts.mapping.latitude || opts.mapping.longitude)) {
    form.append('mapping', JSON.stringify(opts.mapping));
  }
  return form;
}

async function postMultipart<T>(path: string, form: FormData): Promise<T> {
  const base = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
  const response = await fetch(`${base}${path}`, { method: 'POST', headers: multipartHeaders(), body: form });
  if (!response.ok) {
    let detail: unknown;
    try {
      const body = await response.json();
      detail = body?.detail ?? body;
    } catch {
      detail = await response.text().catch(() => null);
    }
    const err = new Error(
      typeof detail === 'string' && detail ? detail : `Request failed (${response.status})`
    ) as Error & { response: { data: { detail: unknown } } };
    err.response = { data: { detail } };
    throw err;
  }
  return (await response.json()) as T;
}

export async function previewReferenceLayerFile(
  file: File,
  opts: ReferenceLayerImportOptions = {}
): Promise<ReferenceLayerPreviewDto> {
  return postMultipart<ReferenceLayerPreviewDto>('/reference-layers/preview', buildForm(file, opts));
}

export async function uploadReferenceLayerFile(
  file: File,
  opts: ReferenceLayerImportOptions = {}
): Promise<ReferenceLayerDto> {
  return postMultipart<ReferenceLayerDto>('/reference-layers', buildForm(file, opts));
}
